import { Button, Card, Label, Select, Spinner } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listClients, getClientCompanies, getClient, type Client, type Company } from '../api/clients';

interface ClientCompanySelectorProps {
  onCancel?: () => void;
  className?: string;
}

export const ClientCompanySelector: React.FC<ClientCompanySelectorProps> = ({
  onCancel,
  className = ''
}) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load clients on mount
  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      setError(null);
      try {
        const response = await listClients();
        if (!response.success) {
          throw new Error(response.message || 'Failed to load clients');
        }
        setClients(response.data.Records);
      } catch (err) {
        console.error('Failed to load clients:', err);
        setError(err instanceof Error ? err.message : 'Failed to load clients');
      } finally {
        setIsLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  // Load companies when client is selected
  useEffect(() => {
    const loadCompanies = async () => {
      if (!selectedClientId) {
        setCompanies([]);
        setSelectedCompanyId('');
        return;
      }

      setIsLoadingCompanies(true);
      setError(null);
      try {
        const response = await getClientCompanies(selectedClientId);
        if (!response.success) {
          throw new Error(response.message || 'Failed to load companies');
        }
        // Companies are directly in Records array
        setCompanies(response.data.Records);
        setSelectedCompanyId(''); // Reset company selection when client changes
      } catch (err) {
        console.error('Failed to load companies:', err);
        setError(err instanceof Error ? err.message : 'Failed to load companies');
        setCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [selectedClientId]);

  const handleNavigateToClient = useCallback(async () => {
    if (!selectedClientId || !selectedCompanyId) {
      setError('Please select both a client and company');
      return;
    }

    setIsNavigating(true);
    setError(null);
    try {
      // Store client context in localStorage
      localStorage.setItem('selectedClientId', selectedClientId);
      localStorage.setItem('selectedCompanyId', selectedCompanyId);

      // Brief delay to show "Navigating..." state
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to set client context:', err);
      setError(err instanceof Error ? err.message : 'Failed to navigate to client dashboard');
      setIsNavigating(false);
    }
  }, [selectedClientId, selectedCompanyId, navigate]);

  if (isLoadingClients) {
    return (
      <Card className={`p-6 bg-white ${className}`}>
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-700">Loading clients...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 bg-white ${className}`}>
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select Client & Company
          </h2>
          <p className="text-gray-600">
            Choose a client and company to access their dashboard
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="client-select" value="Client" />
            <Select
              id="client-select"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              disabled={isLoadingClients}
            >
              <option value="">Select a client...</option>
              {clients.map((client) => (
                <option key={client.Client_ID} value={client.Client_ID}>
                  {client.Client_Name} ({client.Client_ID})
                </option>
              ))}
            </Select>
          </div>

          {selectedClientId && (
            <div>
              <Label htmlFor="company-select" value="Company" />
              <Select
                id="company-select"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={isLoadingCompanies}
              >
                <option value="">
                  {isLoadingCompanies ? 'Loading companies...' : 'Select a company...'}
                </option>
                {companies.map((company) => (
                  <option key={company.Company_ID} value={company.Company_ID}>
                    {company.Company_Name} ({company.Company_ID})
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <strong>Client Context:</strong> You will be able to access client-specific data and configurations.
          </p>
          <ul className="text-xs text-blue-700 mt-2 space-y-1">
            <li>• View and manage client-specific settings</li>
            <li>• Access company data and workflows</li>
            <li>• View analytics and reports</li>
            <li>• Switch clients anytime from settings</li>
          </ul>
        </div>

        <div className="flex justify-end space-x-3">
          {onCancel && (
            <Button color="gray" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            color="blue"
            onClick={handleNavigateToClient}
            disabled={!selectedClientId || !selectedCompanyId || isNavigating}
          >
            {isNavigating ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Navigating...
              </>
            ) : (
              'Go to Client'
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ClientCompanySelector;
